import { html, nothing, type TemplateResult } from "lit";
import type { Engine } from "../../engine/Engine";
import type { Material, Object3D, Scene } from "three";
import { renderSceneDebugMaterialMaps } from "./SceneDebugMaterialMaps";
import {
  applyPosition,
  applyRotationDeg,
  applyScalarField,
  applyScale,
  applyVisible,
  countMaterialUsers,
  listScalarFields,
  objectMaterials,
  rotationDeg,
  type SceneDebugScalarField,
} from "./sceneDebugModel";

export type SceneDebugInspectPanel = "transform" | "material";

export function renderSceneDebugInspector(opts: {
  object: Object3D | undefined;
  scene: Scene | undefined;
  engine?: Engine;
  panel: SceneDebugInspectPanel;
  onPanel: (panel: SceneDebugInspectPanel) => void;
  urlDrafts: Record<string, string>;
  onUrlDraft: (key: string, value: string) => void;
  onChanged: () => void;
}): TemplateResult {
  const object = opts.object;
  if (!object) {
    return html`<p class="sd-empty">Select an object in the tree.</p>`;
  }

  const materials = objectMaterials(object);

  return html`
    <div class="sd-tabs" role="tablist" aria-label="Inspector">
      <button
        type="button"
        role="tab"
        class="sd-tab ${opts.panel === "transform" ? "is-active" : ""}"
        aria-selected=${opts.panel === "transform" ? "true" : "false"}
        @click=${() => opts.onPanel("transform")}
      >
        Transform
      </button>
      <button
        type="button"
        role="tab"
        class="sd-tab ${opts.panel === "material" ? "is-active" : ""}"
        aria-selected=${opts.panel === "material" ? "true" : "false"}
        @click=${() => opts.onPanel("material")}
      >
        Material
        ${materials.length
          ? html`<span class="sd-tab-count">${materials.length}</span>`
          : nothing}
      </button>
    </div>
    <div class="sd-panel" role="tabpanel">
      ${opts.panel === "transform"
        ? renderTransformPanel(object, opts)
        : renderMaterialsPanel(materials, opts)}
    </div>
  `;
}

function renderTransformPanel(
  object: Object3D,
  opts: { engine?: Engine; onChanged: () => void },
): TemplateResult {
  const rot = rotationDeg(object);
  return html`
    <p class="sd-readonly">${object.name || object.type}</p>
    ${vec3Row("position", [object.position.x, object.position.y, object.position.z], (axis, value) => {
      applyPosition(object, axis, value, opts.engine);
      opts.onChanged();
    })}
    ${vec3Row("rotation", rot, (axis, value) => {
      applyRotationDeg(object, axis, value, opts.engine);
      opts.onChanged();
    })}
    ${vec3Row("scale", [object.scale.x, object.scale.y, object.scale.z], (axis, value) => {
      applyScale(object, axis, value, opts.engine);
      opts.onChanged();
    })}
    <label class="sd-check sd-check-row">
      <input
        type="checkbox"
        .checked=${object.visible}
        @change=${(event: Event) => {
          applyVisible(
            object,
            (event.currentTarget as HTMLInputElement).checked,
            opts.engine,
          );
          opts.onChanged();
        }}
      />
      visible
    </label>
  `;
}

function renderMaterialsPanel(
  materials: Material[],
  opts: {
    scene: Scene | undefined;
    engine?: Engine;
    urlDrafts: Record<string, string>;
    onUrlDraft: (key: string, value: string) => void;
    onChanged: () => void;
  },
): TemplateResult {
  if (!materials.length) {
    return html`<p class="sd-empty">No materials on this object.</p>`;
  }
  return html`${materials.map((material, index) =>
    renderMaterial(material, index, materials.length, opts),
  )}`;
}

function renderMaterial(
  material: Material,
  index: number,
  total: number,
  opts: {
    scene: Scene | undefined;
    engine?: Engine;
    urlDrafts: Record<string, string>;
    onUrlDraft: (key: string, value: string) => void;
    onChanged: () => void;
  },
): TemplateResult {
  const users = countMaterialUsers(opts.scene, material);
  const fields = listScalarFields(material);

  return html`
    <section class="sd-block">
      <h3>Material ${total > 1 ? index + 1 : ""}</h3>
      <p class="sd-readonly">${users} mesh use this uuid</p>
      <div class="sd-fields">
        ${fields.map((field) => renderField(field, material, opts))}
      </div>
      <h4>Maps</h4>
      ${renderSceneDebugMaterialMaps({
        material,
        engine: opts.engine,
        urlDrafts: opts.urlDrafts,
        onUrlDraft: opts.onUrlDraft,
        onChanged: opts.onChanged,
      })}
    </section>
  `;
}

function renderField(
  field: SceneDebugScalarField,
  material: Material,
  opts: { engine?: Engine; onChanged: () => void },
): TemplateResult {
  if (field.kind === "readonly") {
    return html`
      <label class="sd-field">
        <span>${field.label}</span>
        <output>${field.value}</output>
      </label>
    `;
  }

  if (field.kind === "boolean") {
    return html`
      <label class="sd-field sd-field-inline">
        <span>${field.label}</span>
        <input
          type="checkbox"
          .checked=${field.value}
          @change=${(event: Event) => {
            applyScalarField(
              material,
              field,
              (event.currentTarget as HTMLInputElement).checked,
              opts.engine,
            );
            opts.onChanged();
          }}
        />
      </label>
    `;
  }

  if (field.kind === "color") {
    return html`
      <label class="sd-field">
        <span>${field.label}</span>
        <input
          type="color"
          .value=${field.value}
          @input=${(event: Event) => {
            applyScalarField(
              material,
              field,
              (event.currentTarget as HTMLInputElement).value,
              opts.engine,
            );
            opts.onChanged();
          }}
        />
      </label>
    `;
  }

  if (field.kind === "enum") {
    return html`
      <label class="sd-field">
        <span>${field.label}</span>
        <select
          .value=${field.value}
          @change=${(event: Event) => {
            applyScalarField(
              material,
              field,
              (event.currentTarget as HTMLSelectElement).value,
              opts.engine,
            );
            opts.onChanged();
          }}
        >
          ${field.options.map(
            (option) => html`
              <option value=${option.value} ?selected=${option.value === field.value}>
                ${option.label}
              </option>
            `,
          )}
        </select>
      </label>
    `;
  }

  if (field.kind === "vec2") {
    return html`
      <label class="sd-field">
        <span>${field.label}</span>
        <span class="sd-xyz">
          ${(["x", "y"] as const).map(
            (axis, i) => html`
              <input
                type="number"
                step="0.01"
                .value=${String(field.value[i])}
                @change=${(event: Event) => {
                  const next: [number, number] = [...field.value];
                  next[i] = Number((event.currentTarget as HTMLInputElement).value);
                  applyScalarField(material, field, next, opts.engine);
                  opts.onChanged();
                }}
                aria-label=${`${field.label} ${axis}`}
              />
            `,
          )}
        </span>
      </label>
    `;
  }

  if (field.kind === "number") {
    return html`
      <label class="sd-field">
        <span>${field.label}</span>
        <input
          type="number"
          min=${field.min ?? nothing}
          max=${field.max ?? nothing}
          step=${field.step ?? 0.01}
          .value=${String(field.value)}
          @change=${(event: Event) => {
            applyScalarField(
              material,
              field,
              Number((event.currentTarget as HTMLInputElement).value),
              opts.engine,
            );
            opts.onChanged();
          }}
        />
      </label>
    `;
  }

  return html`${nothing}`;
}

function vec3Row(
  label: string,
  values: [number, number, number] | number[],
  onChange: (axis: "x" | "y" | "z", value: number) => void,
): TemplateResult {
  const axes = ["x", "y", "z"] as const;
  return html`
    <div class="sd-field sd-field-vec">
      <span>${label}</span>
      <span class="sd-xyz">
        ${axes.map(
          (axis, index) => html`
            <label class="sd-axis">
              <span>${axis}</span>
              <input
                type="number"
                step="0.01"
                .value=${String(Number(values[index].toFixed(4)))}
                @change=${(event: Event) =>
                  onChange(
                    axis,
                    Number((event.currentTarget as HTMLInputElement).value),
                  )}
                aria-label=${`${label} ${axis}`}
              />
            </label>
          `,
        )}
      </span>
    </div>
  `;
}
